import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import GenericButton from '../../components/updated/shared/Button';
import CustomSelectBox from '../../components/updated/Deposit/CustomSelect';
import InputField from '../../components/updated/shared/TextField';
import DepositCryptoLayout from '../../components/updated/DepositCrypto';
import { useTheme } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import {
  decodeJWT,
  checkAndUpdateDeposit,
  getUserWallets,
} from '../../services/api';
import useCopyToClipboard from '../../utils/useCopyToClipboard';
import initialTokens from '../../utils/Tokens.json';
import GeneralPopup from '../../components/updated/BuySell/Popup';

const useStyle = makeStyles((theme) => ({
  enterAmountRoot: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    alignItems: 'stretch',
    justifyContent: 'stretch',
  },
  heading: {
    fontSize: '30px',
    fontWeight: '600',
    color: `${theme.palette.text.primary} !important`,
    textAlign: 'left',
  },
  label: {
    color: `${theme.palette.text.primary} !important`,
    fontSize: '14px',
  },
  depositContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '12px',
    display: 'flex',
    gap: '10px',
    padding: '20px 15px',
    alignItems: 'center',
    width: '100%',
    '& h6': {
      fontSize: '16px',
      color: `${theme.palette.text.primary} !important`,
    },
    '& p': {
      fontSize: '12px',
      color: `${theme.palette.text.secondary} !important`,
    },
  },
  text: {
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(3),
  },
  gridContainer: {
    marginBottom: theme.spacing(2),
  },
  gridItem: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  gridHeading: {
    color: theme.palette.text.secondary,
  },
  gridText: {
    color: theme.palette.text.primary,
  },
  listItem: {
    fontSize: '15px',
    color: theme.palette.text.primary,
  },
  copyButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    cursor: 'pointer',
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  warning: {
    color: 'red',
  },
}));

// const defaultNetwork = {
//   WIBS: 'Ethereum',
//   INEX: 'BNB',
//   IN500: 'BNB',
//   'IUSD+': 'BNB',
//   INXC: 'BNB',
// };

// const currencyToNetwork = {
//   INEX: 'BNB',
//   IN500: 'BNB',
//   INXC: 'BNB',
//   'IUSD+': 'BNB',
//   ETH: 'ETH',
//   DOGE: 'DOGE',
//   XRP: 'XRP',
//   BNB: 'BNB',
//   BTC: 'BTC',
//   FTT: 'BNB',
// };

const SUPPORTED_COINS = {
  USDC: {
    networks: {
      Ethereum: '0x3d45D8F3CAcF26f4460732EaFDd468B7FE89E6CC',
      Solana: 'F2JQx6S7uHWGSaHaFX5uUcSR7pYiRMJPoCwgddPz5t7o',
    },
  },
  USDT: {
    networks: {
      Ethereum: '0x3d45D8F3CAcF26f4460732EaFDd468B7FE89E6CC',
      Solana: 'F2JQx6S7uHWGSaHaFX5uUcSR7pYiRMJPoCwgddPz5t7o',
    },
  },
};

const DEFAULT_NETWORK = 'Ethereum';
const COIN_LIST = Object.keys(SUPPORTED_COINS);

const DepositSelectCoin = () => {
  const classes = useStyle();
  const theme = useTheme();
  const [selectedCurrency, setSelectedCurrency] = useState(COIN_LIST[0]);
  const [network, setNetwork] = useState(DEFAULT_NETWORK);
  const [depositHash, setDepositHash] = useState('');
  const [copiedValue, copy] = useCopyToClipboard();
  const [popupMessage, setPopupMessage] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [usersWallets, setUsersWallets] = useState([]);
  const [singleWallet, setSingleWallet] = useState(null);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('access_token');

    // Check if token exists and is valid
    if (!token || token === 'null' || token === 'undefined') {
      console.log('No valid token found, skipping wallet fetch');
      return;
    }

    const decodedToken = decodeJWT(String(token));

    // Check if token was successfully decoded and has email
    if (!decodedToken || !decodedToken.email) {
      console.log('Invalid token or missing email, skipping wallet fetch');
      return;
    }

    getUserWallets(decodedToken.email)
      .then((res) => {
        setUsersWallets(res.data);
        const userWallet = res.data.filter(
          (x) => x.coinSymbol === selectedCurrency
        );
        setSingleWallet(userWallet[0]);
      })
      .catch((error) => {
        console.error('Error fetching user wallets:', error);
      });
  }, [selectedCurrency]);

  useEffect(() => {
    if (selectedCurrency) {
      const defaultNet = singleWallet?.coinNetwork;
      setNetwork(defaultNet);
    }
  }, [selectedCurrency, singleWallet]);

  const handleCurrencyChange = (value) => {
    const chosenCurrency = String(value);
    setSelectedCurrency(chosenCurrency);
    const walletForCurrency = usersWallets.find(
      (wallet) => wallet.coinSymbol === chosenCurrency
    );
    setSingleWallet(walletForCurrency || null);

    const supportedNetworks = Object.keys(
      SUPPORTED_COINS[chosenCurrency]?.networks || {}
    );
    const walletNetwork = walletForCurrency?.coinNetwork;
    const preferredNetwork =
      walletNetwork && supportedNetworks.includes(walletNetwork)
        ? walletNetwork
        : supportedNetworks[0] || DEFAULT_NETWORK;
    setNetwork(preferredNetwork);
    setShowWarning(false);
  };

  const handleNetworkChange = (value) => {
    // if (
    //   (selectedCurrency === 'WIBS' && value !== 'ETH') ||
    //   (['INEX', 'IN500', 'IUSD+', 'INXC'].includes(selectedCurrency) && value !== 'BNB')
    // ) {
    //   setShowWarning(true);
    // } else {
    setNetwork(value);
    setShowWarning(false);
    //}
  };

  const getNetworkItems = () => {
    return Object.keys(SUPPORTED_COINS[selectedCurrency]?.networks || {});
  };

  const copyToClipboard = (text) => {
    copy(text);
    //OpenNotification('success', 'Copied to clipboard');
  };

  const updateDepositTx = (e) => {
    setDepositHash(e.target.value);
  };

  const handlePopupClose = () => {
    setShowPopup(false);
    setPopupMessage('');
  };

  const submitDepositRequest = async () => {
    const token = localStorage.getItem('access_token');

    // Check if token exists and is valid
    if (!token || token === 'null' || token === 'undefined') {
      console.log('No valid token found, cannot submit deposit');
      return;
    }

    const decodedToken = decodeJWT(String(token));

    // Check if token was successfully decoded and has email
    if (!decodedToken || !decodedToken.email) {
      console.log('Invalid token or missing email, cannot submit deposit');
      return;
    }

    setIsLoading(true);
    const res = await checkAndUpdateDeposit(
      decodedToken.email,
      depositHash,
      String(selectedCurrency),
      String(network)
    );

    if (res.status === 200) {
      setPopupMessage('Your deposit is successful.');
      setIsLoading(false);
    } else {
      setPopupMessage(res.data.message);
      setIsLoading(false);
    }
    setDepositHash('');
    setShowPopup(true);
  };

  const selectedAddress =
    SUPPORTED_COINS[selectedCurrency]?.networks?.[network] || '';

  return (
    <DepositCryptoLayout>
      <div className={classes.enterAmountRoot}>
        <h3 className={classes.heading}>Select Coin</h3>
        <h4 className={classes.label}>Coin</h4>
        <CustomSelectBox
          items={initialTokens
            .filter((token) => !token.isStock && !token.isETF)
            .map((token) => token.title)}
          type={'Coin'}
          defaultValue={COIN_LIST[0]}
          onCurrencyChange={handleCurrencyChange}
        />
        <div style={{ margin: '10px' }}></div>
        <h3 className={classes.heading}>Deposit to</h3>
        <h4 className={classes.label}>Network</h4>
        <CustomSelectBox
          items={getNetworkItems()}
          type={'network'}
          onCurrencyChange={handleNetworkChange}
          defaultValue={network}
          key={selectedCurrency}
        />
        <p className={classes.heading} style={{ marginTop: theme.spacing(4) }}>
          Address
        </p>
        <div
          style={{
            display: 'flex',
            marginTop: theme.spacing(1),
            gap: '4px',
            marginBottom: theme.spacing(2),
          }}
        >
          <span
            style={{ color: theme.palette.text.primary, fontWeight: '500' }}
          >
            {selectedAddress}{' '}
          </span>
          <div
            className={classes.copyButton}
            onClick={() => copyToClipboard(selectedAddress)}
          >
            <ContentCopy
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.primary.main,
                },
              }}
              fontSize="small"
            />
          </div>
        </div>
        <div container className={classes.gridContainer}>
          <div item xs={6} className={classes.gridItem}>
            <div>
              <p className={classes.gridHeading}>Expected arrival</p>
              <p className={classes.gridText}>3 network confirmations</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p className={classes.gridHeading}>Expected Unlock</p>
              <p className={classes.gridText}>
                <span style={{ color: theme.palette.primary.main }}>2</span>{' '}
                network confirmations
              </p>
            </div>
          </div>
          <div item xs={6} className={classes.gridItem}>
            <div>
              <p className={classes.gridHeading}>Minimum deposit</p>
              <p className={classes.gridText}> 0.0001 {selectedCurrency} </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p className={classes.gridHeading}>Selected Wallet</p>
              <p className={classes.gridText}>Asset Wallet </p>
            </div>
          </div>
        </div>
        <ul>
          <li className={classes.listItem}>
            Send only {selectedCurrency} to this deposit address.
          </li>
          <li className={classes.listItem}>
            Ensure the network is
            <span
              style={{
                color: theme.palette.primary.main,
              }}
            >
              {' '}
              {network}.
            </span>{' '}
          </li>
          <li className={classes.listItem}>
            Do not send NFTs to this address.{' '}
          </li>
        </ul>
        <InputField
          type={'text'}
          placeholder="Enter deposit transaction hash"
          style={{ height: '55px' }}
          onChange={updateDepositTx}
        />
        <div style={{ margin: '10px' }}>
          {showWarning && (
            <div className={classes.warning}>
              Please use the default network for {selectedCurrency} (
              {singleWallet?.coinNetwork}).
            </div>
          )}
        </div>
        <GenericButton
          text={'Submit deposit transaction hash'}
          styles={{ marginTop: 'auto' }}
          onClick={submitDepositRequest}
          disabled={!depositHash}
          loading={isLoading}
        />
      </div>
      {showPopup && (
        <GeneralPopup message={popupMessage} onClose={handlePopupClose} />
      )}
    </DepositCryptoLayout>
  );
};

export default DepositSelectCoin;
